import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { Lightbulb, Edit3, Upload } from 'lucide-react'

const STEPS = [
  { name: 'Idea', icon: Lightbulb },
  { name: 'Editing', icon: Edit3 },
  { name: 'Share', icon: Upload },
]

export default function CreatorStepper() {
  // your store value (likely 1 when you expect "Idea")
  const activeStep = useSelector((state: RootState) => state.stepper.activeStep)

  // Map store step -> UI step:
  // If your store uses 1 = Idea, 2 = Editing, 3 = Publish,
  // we convert it to 0-based indexes for the STEPS array.
  const uiActive = Math.max(0, activeStep - 1)

  return (
    <nav
      aria-label="Progress"
      className="backdrop-blur-md p-4 rounded-2xl bg-white/5 border border-white/10 shadow-lg"
    >
      <ol role="list" className="flex items-center justify-center gap-6">
        {STEPS.map((step, index) => {
          const isCompleted = index < uiActive
          const isActive = index === uiActive
          const Icon = step.icon

          return (
            <li key={step.name} className="flex items-center">
              <div className="relative flex items-center justify-center">
                {/* Rainbow glow only on active */}
                <div
                  aria-hidden
                  className={`absolute -inset-2 rounded-xl blur-xl pointer-events-none transition-all duration-500
                    ${isActive ? 'opacity-100 scale-105' : 'opacity-10 scale-95'}`}
                  style={{
                    background:
                      'conic-gradient(from 180deg at 50% 50%, rgba(236,72,153,0.8), rgba(249,115,22,0.8), rgba(59,130,246,0.8), rgba(236,72,153,0.8))',
                  }}
                />

                <div
                  aria-current={isActive ? 'step' : undefined}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all border backdrop-blur-sm shadow-lg
                    ${isActive ? 'bg-white/10 border-white/15 ring-1 ring-white/20' : 'bg-white/3 border-white/5 opacity-60'}`}
                >
                  <Icon className={`h-4 w-4 relative z-10 ${isActive ? 'text-white' : 'text-white/40'}`} />
                </div>
              </div>

              {/* Label wrapper — lets us animate/hover the label separately */}
              <div className="ml-3">
                <span
                  className={`text-sm font-medium transition-all duration-200 transform
                    ${isActive ? 'text-white text-opacity-100 scale-100' : 'text-white/50 opacity-70 scale-95 hover:opacity-100 hover:text-white hover:scale-100'}
                    ${isCompleted ? 'text-white/70' : ''}`}
                >
                  {step.name}
                </span>
              </div>

              {/* Connector Line */}
              {index < STEPS.length - 1 && (
                <div
                  className={`mx-4 h-0.5 w-12 rounded-full transition-all duration-300 ${
                    isCompleted ? 'bg-white/40' : 'bg-white/10 opacity-30'
                  }`}
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
